import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { AxiosError } from 'axios';
import {
  EnvironmentVariables,
  getEnvironmentVariable,
  isProdEnv,
} from '@Services/env/environment.service';
import {
  captureLogRocketException,
  getLogRocketSessionURL,
} from '@Services/analytics/logrocket.service';
import { useBoundStore } from '@Store/useBoundStore';
import { findMappedError } from '@Services/errors/errorsMap.ts';
import { ErrorType } from '@Services/errors/errorType.ts';
import { version } from '../../../package.json';
import type { Client } from '@bugsnag/js';

export let bugsnagClient: Client;
const IGNORED_NETWORK_URLS = [
  'analytics',
  'mixpanel',
  'api.fpjs.io',
  'bugsnag',
  'google',
  'socure',
];
let isPerformanceInitialized = false;

export const ObservabilityInit = () => {
  const getCustomerData = useBoundStore.getState().getCustomerData;
  bugsnagClient = Bugsnag.start({
    apiKey: getEnvironmentVariable(EnvironmentVariables.VITE_APP_BUG_SNAG),
    plugins: [new BugsnagPluginReact()],
    onError: function (event) {
      if (event.originalError instanceof AxiosError) {
        const axiosError = event.originalError;
        const errorCode = axiosError.response?.data?.errors?.[0]?.code;
        const httpStatus = axiosError.response?.status;

        if (errorCode && findMappedError(errorCode)?.errorType === ErrorType.BUSINESS_LOGIC) {
          return false; // Do not send business logic errors to Bugsnag
        }

        if (!errorCode && httpStatus === 401) {
          event.severity = 'warning';
          event.errors[0].errorMessage = 'Token expired - Raw HTTP 401 (Expected)';
          event.groupingHash = 'token-expired-401-errors';
        }
      }

      const customer = getCustomerData();
      event.setUser(
        customer.customerId,
        customer.customerData.email,
        customer.customerData.firstName
      );

      if (event.originalError instanceof Error) {
        captureLogRocketException(event.originalError);
      }

      return true; // Send technical errors to Bugsnag
    },
  });

  getLogRocketSessionURL((sessionURL) => {
    bugsnagClient.addMetadata('logrocket', { sessionURL });
  });

  if (isProdEnv() && !isPerformanceInitialized) {
    isPerformanceInitialized = true;

    BugsnagPerformance.start({
      apiKey: getEnvironmentVariable(EnvironmentVariables.VITE_APP_BUG_SNAG),
      appVersion: version,
      autoInstrumentRouteChanges: true, // Track route changes automatically
      autoInstrumentNetworkRequests: true, // Track fetch/XHR requests
      networkRequestCallback: (networkRequestInfo) => {
        // Check if URL isn't from lili.co or contains any ignored keyword urls
        if (
          !networkRequestInfo?.url?.includes('lili.co') ||
          IGNORED_NETWORK_URLS.some((keyword) => networkRequestInfo?.url?.includes(keyword))
        ) {
          return null; // Don't track this request
        }
        return networkRequestInfo; // Track this request
      },
    });
  }
};

export const reportError = (error: IObservabilityError) => {
  if (!bugsnagClient) {
    console.error('Bugsnag client is not initialized');
    return;
  }
  const getCustomerData = useBoundStore.getState().getCustomerData;
  const customer = getCustomerData();

  bugsnagClient.notify(`${error?.metadata?.name} - ${error?.metadata?.message}`, (event) => {
    event.setUser(
      customer.customerId,
      customer.customerData.email,
      customer.customerData.firstName
    );
    const severityMap: { [key in ObservabilityErrorSeverity]: 'info' | 'warning' | 'error' } = {
      [ObservabilityErrorSeverity.INFO]: 'info',
      [ObservabilityErrorSeverity.WARNING]: 'warning',
      [ObservabilityErrorSeverity.ERROR]: 'error',
    };
    event.severity = severityMap[error.severity];
    if (error.groupingHash) {
      event.groupingHash = error.groupingHash;
    }
  });

  captureLogRocketException(new Error(`${error?.metadata?.name} - ${error?.metadata?.message}`));
};

export interface IObservabilityError {
  metadata: {
    name: string;
    message: string;
  };
  severity: ObservabilityErrorSeverity;
  groupingHash?: string;
}

export enum ObservabilityErrorSeverity {
  INFO,
  WARNING,
  ERROR,
}
